// IM-XPR-002 (IM-UI-097, IM-UX-110..114) — "Conhecer meus concorrentes": a J2 (16 etapas) em cinco macrofases
// (Encontrar → Confirmar → Conhecer → Comparar → Acompanhar). Selecionar uma fase troca só o painel contextual. Decisões
// simples acontecem aqui, pelas rotas donas: classificar (IM-API de concorrentes), montar o dossiê (battlecard por regras,
// sem IA), definir o papel na comparação e ativar páginas recomendadas. O que exige tela especializada (cadastrar e
// pesquisar concorrente, revisar itens do dossiê, decidir na Central) abre a tela certa com retorno ao objetivo.
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  J2_CONCORRENTES_V1, POSITIONING_ROLE_LABEL, objectiveOf, phaseStatusOf, stepDeps, type CompetitorDetailDto, type CompetitorStatus, type JourneyDto, type MacroPhaseView, type PositioningDto, type PositioningRole,
} from '@intelimove/contracts';
import { ApiError } from '../api/client';

const J2_DEPS = stepDeps(J2_CONCORRENTES_V1);
import { AsyncView } from '../components/States';
import { useToast } from '../components/Toast';
import { initials } from '../components/Widgets';
import { battlecardApi } from '../data/battlecards';
import { COMPETITOR_STATUS_TXT, competitorAdapter, researchState, SUGGESTION_TXT } from '../data/competitors';
import { fmtDate } from '../data/intel';
import { useResource } from '../hooks/useResource';
import { useCanManage } from '../pages/Targets';
import { useExperience } from './ExperienceContext';
import {
  loadCompetitorFacts, loadCompetitorWatch, loadObjectiveJourney, loadPageRecommendations, phaseConsequence, phaseHeadline, phasesOf, plural,
  type CompetitorFacts, type CompetitorWatch, type PagesData,
} from './data';
import { ObjectiveFlow, type FlowPhase } from './ObjectiveFlow';
import { ObjectiveHead, openStepOf, ProcessOverview, StartButton, StepsDetail, usePhase } from './ObjectiveWorkspace';
import { topPages } from './pages';
import { RecommendedPages } from './RecommendedPages';
import { ContextLink } from './returnTo';

const DEF = objectiveOf('concorrentes')!;
const COMPETITORS = '/configuracoes/contexto/concorrentes';
type Data = {
  journey: JourneyDto | null; facts: CompetitorFacts; cards: Awaited<ReturnType<typeof loadCompetitorWatch>>['cards']; watch: CompetitorWatch[];
  positioning: PositioningDto | null; pages: PagesData;
};
const ORIGIN: Record<string, string> = { USER: 'Informado por você', ENRICHMENT: 'Sugerido pela pesquisa da sua empresa', SITE_ANALYSIS: 'Sugerido pela pesquisa da sua empresa', MARKET_ANALYSIS: 'Encontrado na análise de mercado' };
const MAX = 5;

export function CompetitorsObjective() {
  const xp = useExperience();
  const { state, reload, refresh } = useResource(async (s): Promise<Data> => {
    const [jr, facts, w, positioning] = await Promise.all([
      loadObjectiveJourney('concorrentes', xp.journeys, s).catch(() => ({ catalog: null, journey: null })),
      loadCompetitorFacts(s),
      loadCompetitorWatch(s),
      battlecardApi.positioning(s).catch(() => null),
    ]);
    const pages = await loadPageRecommendations(s, w.watch).catch(() => ({ all: [], monitored: [] }));
    return { journey: jr.journey, facts, cards: w.cards, watch: w.watch, positioning, pages };
  }, [xp.journeys]);
  return (
    <section aria-labelledby="page-title">
      <AsyncView state={state} onRetry={reload} empty={null} loading="metrics">
        {(d) => <Workspace d={d} refresh={refresh} reload={reload} journeysOn={xp.journeys} />}
      </AsyncView>
    </section>
  );
}

function countFor(key: string, d: Data): string | null {
  const f = d.facts;
  const withCard = d.cards.filter((x) => x.battlecard?.status === 'ACTIVE' && x.competitor.status === 'CONFIRMED').length;
  const roles = d.positioning ? Object.values(d.positioning.groups).flat() : [];
  switch (key) {
    case 'encontrar': return f.items.length ? plural(f.items.length, 'empresa', 'empresas') : null;
    case 'confirmar': return f.toClassify.length ? `${f.toClassify.length} para classificar` : f.confirmed ? plural(f.confirmed, 'confirmada', 'confirmadas') : null;
    case 'conhecer': return f.confirmed ? `${withCard} de ${f.confirmed} com dossiê` : null;
    case 'comparar': return roles.length ? `${roles.filter((r) => r.roleDecided).length} de ${roles.length} com papel` : null;
    case 'acompanhar': { const top = topPages(d.pages.all).top.length; const active = d.watch.reduce((n, w) => n + w.monitoring.sources.filter((s) => (s.state ? s.state.countsAsActive : s.status === 'active')).length, 0); return top ? plural(top, 'recomendada', 'recomendadas') : active ? plural(active, 'página ativa', 'páginas ativas') : null; }
  }
  return null;
}

function Workspace({ d, refresh, reload, journeysOn }: { d: Data; refresh: () => void; reload: () => void; journeysOn: boolean }) {
  const phases: MacroPhaseView[] | null = d.journey ? phasesOf(DEF, d.journey) : null;
  const current = phases?.find((p) => p.current);
  const flow: FlowPhase[] = DEF.phases.map((p) => {
    const v = phases?.find((x) => x.key === p.key);
    return { key: p.key, name: p.name, question: p.question, state: v?.state ?? null, current: !!v?.current, count: countFor(p.key, d), status: d.journey ? phaseStatusOf(v, phases, J2_DEPS) : null };
  });
  const [selected, select] = usePhase(DEF.phases.map((p) => p.key), current?.key ?? 'encontrar');
  const back = { to: `/objetivos/concorrentes?fase=${selected}`, label: DEF.title };
  const view = phases?.find((p) => p.key === selected) ?? null;
  const def = DEF.phases.find((p) => p.key === selected)!;
  const headline = view?.current ? phaseHeadline('concorrentes', view, { competitors: d.facts, pages: topPages(d.pages.all).top.length }) : def.question;
  const step = openStepOf(d.journey, def.steps);
  return (
    <>
      <ObjectiveHead def={DEF} journey={d.journey}>
        {d.journey && <ContextLink className="secondary btn-link" to={`/jornadas/${d.journey.journey.id}`} back={back}>Ver etapas detalhadas</ContextLink>}
      </ObjectiveHead>
      <div className="xp-shell">
        <ProcessOverview phases={phases} journey={d.journey} extra={!d.journey && journeysOn ? <StartButton def={DEF} onStarted={reload} /> : null} />
        <ObjectiveFlow id="xp-j2" label="Fases para conhecer seus concorrentes" phases={flow} selected={selected} onSelect={select} unknownLabel="Ainda não começou">
          <article className="xp-panel" aria-labelledby="xp-phase-t">
            <span className="xp-kicker">{view ? (view.current ? 'Sua decisão agora' : view.state === 'CONCLUIDA' ? 'Fase concluída' : view.state === 'CONTINUA' ? 'Acompanhamento contínuo' : 'Fase seguinte') : `Fase ${DEF.phases.indexOf(def) + 1} de 5`}</span>
            <h2 id="xp-phase-t">{headline}</h2>
            <p>{view?.current ? phaseConsequence(phases!, view) ?? def.result : `Ao final: ${def.result}`}</p>
            {selected === 'encontrar' && <Find d={d} back={back} />}
            {selected === 'confirmar' && <Confirm d={d} onDone={refresh} onNext={() => select('conhecer')} />}
            {selected === 'conhecer' && <Know d={d} back={back} onDone={refresh} />}
            {selected === 'comparar' && <Compare d={d} back={back} onDone={refresh} />}
            {selected === 'acompanhar' && <Watch d={d} back={back} onDone={refresh} stepHref={step?.href ?? null} />}
            {step && selected !== 'confirmar' && selected !== 'acompanhar' && step.href && (
              <p className="cell-sub">Próximo passo nesta fase: <ContextLink className="link-btn" to={step.href} back={back}>{step.actionLabel ?? 'Abrir'}</ContextLink></p>
            )}
            <StepsDetail journey={d.journey} ids={def.steps} back={back} />
          </article>
          <Synthesis d={d} selected={selected} />
        </ObjectiveFlow>
      </div>
    </>
  );
}

function Synthesis({ d, selected }: { d: Data; selected: string }) {
  const p = d.positioning;
  // Na fase Comparar, ganhos e riscos já estão no painel: a síntese não repete.
  const opp = selected === 'comparar' ? undefined : p?.opportunities[0];
  const risk = selected === 'comparar' ? undefined : p?.threats[0];
  const top = topPages(d.pages.all).top.length;
  return (
    <aside className="xp-panel" aria-labelledby="xp-syn-t">
      <span className="xp-kicker">Síntese do InteliMove</span>
      <h3 id="xp-syn-t">Seu cenário competitivo</h3>
      {d.facts.confirmed > 0 ? <div className="xp-score"><strong>{d.facts.confirmed}</strong><span>{d.facts.confirmed === 1 ? 'concorrente confirmado' : 'concorrentes confirmados'}{d.facts.hypotheses ? <><br />e {plural(d.facts.hypotheses, 'hipótese', 'hipóteses')}</> : null}</span></div>
        : d.facts.items.length > 0 ? <div className="xp-score"><strong>{d.facts.items.length}</strong><span>{d.facts.items.length === 1 ? 'empresa na lista,' : 'empresas na lista,'}<br />nenhuma confirmada ainda</span></div>
        : <p className="xp-quiet">Nenhum concorrente na lista ainda.</p>}
      {opp && <div className="xp-insight"><b>Oportunidade percebida</b><p>{opp.text} <span className="cell-sub inline">({opp.name})</span></p></div>}
      {risk && <div className="xp-insight"><b>Risco principal</b><p>{risk.text} <span className="cell-sub inline">({risk.name})</span></p></div>}
      {p && p.concentration.level !== 'INDETERMINADA' && <div className="xp-insight"><b>Concentração da disputa</b><p>{p.concentration.explanation}</p></div>}
      {!p?.opportunities.length && !p?.threats.length && <p className="xp-quiet">A síntese aparece quando houver dossiês dos concorrentes confirmados.</p>}
      {selected !== 'acompanhar' && top > 0 && <p className="cell-sub" style={{ marginTop: 14 }}><b>{plural(top, 'página recomendada', 'páginas recomendadas')}</b> para acompanhar. <Link className="link-btn" to="?fase=acompanhar" replace>Ver recomendações</Link></p>}
    </aside>
  );
}

function CompetitorRow({ k, children, sub }: { k: CompetitorDetailDto; children?: ReactNode; sub: ReactNode }) {
  return (
    <li className="xp-row">
      <span className="xp-row-logo" aria-hidden="true">{initials(k.name)}</span>
      <div className="xp-row-main"><b>{k.name}</b>{sub}</div>
      {children && <div className="xp-row-side">{children}</div>}
    </li>
  );
}

function Find({ d, back }: { d: Data; back: { to: string; label: string } }) {
  const canManage = useCanManage();
  const items = d.facts.items;
  const row = (k: CompetitorDetailDto) => {
    const review = !k.domain || k.identityConfidence === 'BAIXA';
    return <CompetitorRow key={k.id} k={k} sub={<><span>{k.domain ?? 'Sem site informado'} · {ORIGIN[k.origin] ?? 'Cadastrado'}</span><span>{COMPETITOR_STATUS_TXT[k.status]?.[0] ?? k.status} · {researchState(k).label}{review ? ' · precisa conferir a identidade' : ''}</span></>} />;
  };
  return (
    <>
      {items.length ? <ul className="xp-list" role="list">{items.slice(0, MAX).map(row)}</ul> : <p className="xp-quiet">Nenhum concorrente na lista ainda.</p>}
      {items.length > MAX && <details className="xp-why xp-more"><summary>Ver todas ({items.length})</summary><ul className="xp-list" role="list">{items.slice(MAX).map(row)}</ul></details>}
      <div className="xp-cta-row">
        {canManage ? <ContextLink className="xp-primary petrol" to={COMPETITORS} back={back}>{items.length ? 'Adicionar ou conferir concorrentes' : 'Informar concorrentes'}</ContextLink> : <span className="cell-sub">Um administrador pode adicionar concorrentes.</span>}
        <ContextLink className="link-btn" to="/mercado" back={back}>Ver candidatos da análise de mercado</ContextLink>
      </div>
    </>
  );
}

const CLASSIFY: Array<[CompetitorStatus, string]> = [['CONFIRMED', 'Concorrente'], ['HYPOTHESIS', 'Manter como hipótese'], ['RELATED', 'Não concorre'], ['DISCARDED', 'Descartar']];
const SUGGESTED: Record<string, CompetitorStatus> = { COMPETITOR: 'CONFIRMED', RELATED: 'RELATED', NOT_COMPETITOR: 'DISCARDED' };

function Confirm({ d, onDone, onNext }: { d: Data; onDone: () => void; onNext: () => void }) {
  const canManage = useCanManage();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [decided, setDecided] = useState<Record<string, CompetitorStatus>>({});
  const open = d.facts.toClassify.filter((k) => !decided[k.id]);
  const classify = async (k: CompetitorDetailDto, status: CompetitorStatus) => {
    setBusy(k.id);
    try {
      await competitorAdapter.classify(k.id, status);
      setDecided((x) => ({ ...x, [k.id]: status }));
      toast(`${k.name}: ${COMPETITOR_STATUS_TXT[status]?.[0] ?? 'classificado'}.`);
      onDone();
    } catch (e) { toast(e instanceof ApiError ? e.message : 'Não foi possível classificar agora.', 'error'); }
    finally { setBusy(null); }
  };
  const done = d.facts.items.filter((k) => !d.facts.toClassify.includes(k) || decided[k.id]);
  return (
    <>
      {open.length ? (
        <>
          <ul className="xp-list" role="list">
            {open.slice(0, MAX).map((k) => {
              const sug = k.suggestedClassification ? SUGGESTED[k.suggestedClassification] : null;
              return (
                <CompetitorRow key={k.id} k={k} sub={<span>{k.suggestedClassification ? `Sugestão da pesquisa: ${SUGGESTION_TXT[k.suggestedClassification]}` : 'Sem sugestão da pesquisa: decida pelo que você conhece'}{k.analysis?.rationale ? ` — ${k.analysis.rationale.slice(0, 140)}` : ''}</span>}>
                  {canManage ? (
                    <div className="xp-choice" role="group" aria-label={`Papel de ${k.name}`}>
                      {CLASSIFY.map(([st, label]) => (
                        <button key={st} type="button" className={sug === st ? 'suggested' : ''} disabled={busy !== null} onClick={() => void classify(k, st)} aria-label={`${label}: ${k.name}${sug === st ? ' (sugestão)' : ''}`}>{label}</button>
                      ))}
                    </div>
                  ) : null}
                </CompetitorRow>
              );
            })}
          </ul>
          {open.length > MAX && <p className="cell-sub">Mais {open.length - MAX} depois destas. Classifique as primeiras para ver as próximas.</p>}
          {!canManage && <p className="cell-sub">Um administrador da organização pode classificar.</p>}
          <p className="cell-sub">Direto, indireto ou substituto você define na fase Comparar. Pode mudar depois.</p>
        </>
      ) : (
        <div className="xp-cta-row">
          <p className="xp-quiet" style={{ margin: 0 }}>{d.facts.items.length ? 'Todas as empresas já têm papel definido.' : 'Primeiro encontre os concorrentes.'}</p>
          {d.facts.items.length > 0 && <button type="button" className="xp-primary petrol" onClick={onNext}>Ir para Conhecer</button>}
        </div>
      )}
      {done.length > 0 && (
        <details className="xp-why">
          <summary>Já classificadas ({done.length})</summary>
          <ul className="plain-list">{done.map((k) => <li key={k.id}><b>{k.name}</b> <span className="cell-sub inline">{COMPETITOR_STATUS_TXT[decided[k.id] ?? k.status]?.[0]}</span></li>)}</ul>
        </details>
      )}
    </>
  );
}

const firstValue = (xs: Array<{ value: string }> | undefined, n = 2) => (xs ?? []).slice(0, n).map((x) => x.value).join('; ');
function Know({ d, back, onDone }: { d: Data; back: { to: string; label: string }; onDone: () => void }) {
  const canManage = useCanManage();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const confirmed = d.facts.items.filter((k) => k.status === 'CONFIRMED');
  const cardOf = (id: string) => d.cards.find((x) => x.competitor.id === id);
  const missing = confirmed.filter((k) => { const c = cardOf(k.id); return c && !c.battlecard && c.eligible; });
  const build = async () => {
    setBusy(true);
    let ok = 0;
    for (const k of missing) { try { await battlecardApi.create(k.id); ok++; } catch (e) { toast(e instanceof ApiError ? `${k.name}: ${e.message}` : `${k.name}: não foi possível montar o dossiê.`, 'error'); } }
    setBusy(false);
    if (ok) toast(ok === 1 ? 'Dossiê montado.' : `${ok} dossiês montados.`);
    onDone();
  };
  if (!confirmed.length) return <p className="xp-quiet">Confirme ao menos um concorrente para o InteliMove montar a síntese de cada um.</p>;
  const row = (k: CompetitorDetailDto) => {
    const c = cardOf(k.id);
    const a = k.analysis;
    const ev = a ? new Set([...a.positioning, ...a.offerings, ...a.differentiators, ...a.favorable, ...a.against].flatMap((x) => x.evidenceIds)).size : 0;
    return (
      <CompetitorRow key={k.id} k={k} sub={a ? (
        <>
          <span><b>Posicionamento:</b> {firstValue(a.positioning, 1) || a.summary || 'não identificado'}</span>
          {a.offerings.length > 0 && <span><b>Ofertas:</b> {firstValue(a.offerings, 3)}</span>}
          {a.audience.length > 0 && <span><b>Público:</b> {firstValue(a.audience)}</span>}
          {a.differentiators.length > 0 && <span><b>Pontos fortes:</b> {firstValue(a.differentiators)}</span>}
          {a.favorable.length > 0 && <span><b>Vulnerabilidades (a seu favor):</b> {firstValue(a.favorable)}</span>}
          <span>{plural(ev, 'evidência', 'evidências')} · pesquisa de {fmtDate(a.analyzedAt)}</span>
        </>
      ) : <span>{researchState(k).label}. A síntese aparece depois da pesquisa.</span>}>
        {c?.battlecard ? <ContextLink to={`/battlecards/${c.battlecard.id}`} back={back} aria-label={`Abrir o dossiê de ${k.name}`}>Abrir dossiê</ContextLink>
          : !a ? <ContextLink to={`${COMPETITORS}?c=${k.id}`} back={back} aria-label={`Pesquisar ${k.name}`}>Pesquisar</ContextLink> : null}
        {c?.battlecard && c.battlecard.pending > 0 && <ContextLink to={`/battlecards/${c.battlecard.id}?aba=revisao`} back={back} aria-label={`Revisar ${c.battlecard.pending} pontos do dossiê de ${k.name}`}>{plural(c.battlecard.pending, 'ponto para revisar', 'pontos para revisar')}</ContextLink>}
      </CompetitorRow>
    );
  };
  return (
    <>
      <ul className="xp-list" role="list">{confirmed.slice(0, MAX).map(row)}</ul>
      {confirmed.length > MAX && <details className="xp-why xp-more"><summary>Ver todos ({confirmed.length})</summary><ul className="xp-list" role="list">{confirmed.slice(MAX).map(row)}</ul></details>}
      {missing.length > 0 && (
        <div className="xp-cta-row">
          {canManage ? <button type="button" className="xp-primary petrol" disabled={busy} aria-busy={busy} onClick={() => void build()}>{busy ? 'Montando…' : missing.length === 1 ? `Montar o dossiê de ${missing[0]!.name}` : `Montar os ${missing.length} dossiês que faltam`}</button>
            : <span className="cell-sub">Um administrador pode montar os dossiês.</span>}
          <span className="cell-sub">O dossiê é montado com a pesquisa já feita, sem nova pesquisa.</span>
        </div>
      )}
    </>
  );
}

const ROLES: PositioningRole[] = ['DIRETO', 'INDIRETO', 'SUBSTITUTO'];
function Compare({ d, back, onDone }: { d: Data; back: { to: string; label: string }; onDone: () => void }) {
  const canManage = useCanManage();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [roles, setRoles] = useState<Record<string, PositioningRole>>({});
  const p = d.positioning;
  if (!p) return <p className="xp-quiet">A comparação aparece quando houver dossiês dos concorrentes confirmados.</p>;
  const list = Object.values(p.groups).flat().filter((x) => x.battlecardId);
  const setRole = async (battlecardId: string, name: string, role: PositioningRole) => {
    setBusy(battlecardId);
    try {
      const card = await battlecardApi.get(battlecardId);
      await battlecardApi.update(battlecardId, card.version, { positioningRole: role });
      setRoles((x) => ({ ...x, [battlecardId]: role }));
      toast(`${name}: ${POSITIONING_ROLE_LABEL[role].toLowerCase()}.`);
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_changes') toast('Esse já era o papel definido.');
      else toast(e instanceof ApiError ? e.message : 'Não foi possível salvar o papel.', 'error');
    }
    finally { setBusy(null); }
  };
  const block = (title: string, items: Array<{ text: string; name?: string }>) => items.length > 0 && (
    <div className="xp-insight"><b>{title}</b>{items.slice(0, 3).map((x, i) => <p key={i}>{x.text}{x.name ? <span className="cell-sub inline"> ({x.name})</span> : null}</p>)}</div>
  );
  return (
    <>
      {list.length ? (
        <ul className="xp-list" role="list">
          {list.slice(0, MAX).map((x) => {
            const role = roles[x.battlecardId!] ?? x.role;
            return (
              <li key={x.competitorId} className="xp-row">
                <span className="xp-row-logo" aria-hidden="true">{initials(x.name)}</span>
                <div className="xp-row-main"><b>{x.name}</b><span>{role ? `Papel: ${POSITIONING_ROLE_LABEL[role]}${x.roleDecided || roles[x.battlecardId!] ? '' : ' (sugestão)'}` : x.roleSuggested ? `Sugestão: ${POSITIONING_ROLE_LABEL[x.roleSuggested]}` : 'Papel ainda não definido'}{x.offerings.length ? ` · disputa ${x.offerings.slice(0, 2).join(', ')}` : ''}</span></div>
                {canManage && (
                  <div className="xp-row-side xp-choice" role="group" aria-label={`Papel de ${x.name} na comparação`}>
                    {ROLES.map((r) => <button key={r} type="button" className={(roles[x.battlecardId!] ?? (x.roleDecided ? x.role : x.roleSuggested)) === r ? 'suggested' : ''} disabled={busy !== null} aria-pressed={role === r && (x.roleDecided || !!roles[x.battlecardId!])} onClick={() => void setRole(x.battlecardId!, x.name, r)}>{POSITIONING_ROLE_LABEL[r]}</button>)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : <p className="xp-quiet">Ainda não há concorrente confirmado com dossiê para comparar.</p>}
      {block('Onde você ganha', p.opportunities)}
      {block('Onde pode perder', p.threats)}
      {block('Diferenciais possíveis', p.differentiation.map((x) => ({ text: x.value, name: x.claimedBy.length ? `também citado por ${x.claimedBy.join(', ')}` : 'ninguém mais cita' })))}
      <div className="xp-cta-row">
        <ContextLink className="xp-primary petrol" to="/battlecards?aba=posicionamento" back={back}>Ver a comparação completa</ContextLink>
        <ContextLink className="link-btn" to="/portfolio" back={back}>Comparar portfólio e preços</ContextLink>
      </div>
    </>
  );
}

function Watch({ d, back, onDone, stepHref }: { d: Data; back: { to: string; label: string }; onDone: () => void; stepHref: string | null }) {
  const changes = d.watch.flatMap((w) => w.monitoring.changes.map((c) => ({ ...c, company: w.competitorName })))
    .sort((a, b) => Number(b.relevant) - Number(a.relevant) || b.at.localeCompare(a.at));
  const active = d.watch.reduce((n, w) => n + w.monitoring.sources.filter((s) => s.status === 'active').length, 0);
  const decision = d.journey?.steps.find((s) => s.stepId === 'J2-15');
  return (
    <>
      <RecommendedPages data={d.pages} onChanged={onDone} />
      <h3 style={{ marginTop: 22 }}>Mudanças recentes</h3>
      {changes.length ? (
        <ul className="xp-list" role="list">
          {changes.slice(0, MAX).map((c) => (
            <li key={c.id} className="xp-row">
              <div className="xp-row-main"><b>{c.title}</b><span><span className="xp-tagline">{c.company}</span>{fmtDate(c.at)} · {c.sourceName}</span>{c.impact && <span><b>Resposta recomendada:</b> {c.impact}</span>}</div>
              {c.signalId && <div className="xp-row-side"><ContextLink to={`/sinais?sinal=${c.signalId}`} back={back}>Ver sinal</ContextLink></div>}
            </li>
          ))}
        </ul>
      ) : <p className="xp-quiet">{active ? `${plural(active, 'página está sendo acompanhada', 'páginas estão sendo acompanhadas')}. Nenhuma mudança detectada ainda.` : 'Ative páginas para o InteliMove avisar quando algo mudar.'}</p>}
      {decision && decision.state !== 'DISPENSADA' && decision.openDecisions > 0 && decision.href && (
        <p className="cell-sub"><b>{plural(decision.openDecisions, 'decisão', 'decisões')}</b> sobre a resposta aos concorrentes. <ContextLink className="link-btn" to={decision.href} back={back}>Decidir na Central</ContextLink></p>
      )}
      {stepHref && <p className="cell-sub">Outros ajustes do acompanhamento: <ContextLink className="link-btn" to={stepHref} back={back}>abrir a tela de monitoramento</ContextLink></p>}
    </>
  );
}
